// Protected Route Component
// Wraps routes that require authentication
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isTokenExpired } from '../utils/authUtils';
import { API_CONFIG } from '../config/api';

// Helper to pull the raw token from storage (same key the API clients use)
const getRawToken = () => {
  try {
    return (
      localStorage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN) ||
      sessionStorage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN)
    );
  } catch {
    return null;
  }
};

// Role permission levels mapping — kept in sync with the backend's
// ROLE_HIERARCHY in src/constants/roles.js
const ROLE_LEVELS = {
  'SUPER_ADMIN': 7,
  'SUPER_ADMIN_USER': 6,
  'TENANT_ADMIN': 5,
  'FLEET_ADMIN': 5,
  'PARTNERSHIP_MANAGER': 4,
  'HOSPITAL_USER': 4,
  'COMMAND_CENTRE_USER': 4,
  'TENANT_USER': 3,
  'DRIVER': 2,
  'INDIVIDUAL_USER': 1
};

// Helper function to check if user needs password reset
const needsPasswordReset = (user) => {
  return user && (user.isFirstTimeLogin || user.requiresPasswordReset);
};

// Helper function to check if user has required role
const hasRequiredRole = (user, requiredRoles) => {
  if (!user || !user.role) {
    return false;
  }
  return requiredRoles.includes(user.role);
};

// Helper function to get access denied reason
const getAccessDeniedReason = (user) => {
  if (!user) return 'No user object';
  if (!user.role) return 'No user role';
  return 'Role not in required roles';
};

// Helper function to render loading state
const renderLoadingState = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
);

// Helper function to render role-based access denied
const renderRoleAccessDenied = (user, requiredRoles) => {
  console.error('❌ [ProtectedRoute] Access denied:', {
    reason: getAccessDeniedReason(user),
    userRole: user?.role,
    requiredRoles
  });

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
        <p className="text-gray-600">You don't have permission to access this page.</p>
        <p className="text-sm text-gray-400 mt-2">
          Your role: {user?.role || 'undefined'} | Required: {requiredRoles.join(', ')}
        </p>
      </div>
    </div>
  );
};

// Helper function to render permission level access denied
const renderPermissionAccessDenied = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
      <p className="text-gray-600">You don't have sufficient permissions to access this page.</p>
    </div>
  </div>
);

// Helper function to get user permission level
const getUserPermissionLevel = (userRole) => {
  return ROLE_LEVELS[userRole] || 0;
};

// Helper function to check role requirements
const checkRoleRequirements = (user, requiredRoles) => {
  if (requiredRoles.length === 0) {
    return null;
  }

  if (!hasRequiredRole(user, requiredRoles)) {
    return renderRoleAccessDenied(user, requiredRoles);
  }

  return null;
};

// Helper function to check permission level requirements
const checkPermissionLevel = (user, requiredPermissionLevel) => {
  if (requiredPermissionLevel === 0) {
    return null;
  }

  const userLevel = getUserPermissionLevel(user?.role);
  if (userLevel < requiredPermissionLevel) {
    return renderPermissionAccessDenied();
  }

  return null;
};

const ProtectedRoute = ({ children, requiredRoles = [], requiredPermissionLevel = 0 }) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (isLoading) {
    return renderLoadingState();
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Validate the stored JWT on every render so a user with a long-lived tab
  // is bounced to /login as soon as their token expires, rather than seeing
  // protected content until they make their next API call.
  const rawToken = getRawToken();
  if (!rawToken || isTokenExpired(rawToken)) {
    return <Navigate to="/login" state={{ from: location, reason: 'session_expired' }} replace />;
  }

  // Check if this is a first-time login (user needs to reset password)
  if (needsPasswordReset(user)) {
    return <Navigate to="/first-time-password-reset" state={{ userData: user, from: location }} replace />;
  }

  // Check role requirements
  const roleCheckResult = checkRoleRequirements(user, requiredRoles);
  if (roleCheckResult) {
    return roleCheckResult;
  }

  // Check permission level
  const permissionCheckResult = checkPermissionLevel(user, requiredPermissionLevel);
  if (permissionCheckResult) {
    return permissionCheckResult;
  }

  // Render the protected component
  return children;
};

export default ProtectedRoute;
